import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CommercialAgreement,
  CreateCommercialAgreementInput,
} from 'src/app/app-sync.service';
import { CommercialAgreementsService } from '../../commercial-agreements.service';
import { UsersService } from 'src/app/pages/users/users.service';
import { MasterService } from 'src/app/pages/master/master.service';
import { atLeastOneDaySelectedValidator } from '../../validators/one-day-selected-validator';
import { formattedNumberRangeValidator } from '../../validators/formatted-number-range-validator';
import { SapIdPipe } from 'src/app/shared/pipes/sap-id.pipe';
import { DAYS } from '../../../../shared/constants/constants';
import { ValidateWorkShiftResponse } from '../../interfaces/workshift';
import { pairwise, startWith } from 'rxjs/operators';
import { WorkShiftService } from '../../services/work-shift.service';
import { percentRangeValidator } from '../../validators/percent-validator';
import { appConstants } from 'src/app/shared/constants/constants';
import { dateMinMonthValidator } from '../../validators/date-month-validator';

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.css'],
})
export class CreateComponent implements OnInit {
  @Input() aC?: CommercialAgreement;
  @Output() modalDismissed: EventEmitter<string> = new EventEmitter<string>();
  @Output() modalClosed: EventEmitter<string> = new EventEmitter<string>();
  createForm: FormGroup;
  business: string;
  master: any = {};
  masterDisplayMap: any = {};
  ACTYPES: any = [];
  entity: any;
  currencies = [
    { value: 'CLP', valueToDisplay: 'Pesos Chilenos' },
    { value: 'USD', valueToDisplay: 'Dolar Americano' },
  ];
  days = DAYS;
  durations = [4, 5, 6, 7, 8, 9, 10, 11, 12];
  hasAdditionalPercent: boolean = false;
  hasGuaranteed: boolean = false;
  listOfWorkShifts: Array<any> = [];
  workShiftsForm: FormGroup;
  hasError: boolean = false;
  hours = Array.from({ length: 24 }, (_, i) => i); // 0 a 23
  minutes = [0, 30]; // solo 00 y 30
  isValidating = false;
  commercialAgreementsStatuces = appConstants.ACstatus;
  commercialAgreementsTypes = appConstants.ACtypes;
  dolarValue: number = 940;
  minDate: string = '';

  constructor(
    private fb: FormBuilder,
    private commercialAgreementsService: CommercialAgreementsService,
    private usersService: UsersService,
    private masterService: MasterService,
    private sapIdPipe: SapIdPipe,
    private workShiftService: WorkShiftService
  ) {
    this.business = this.usersService.business.value.toUpperCase();
    this.minDate = this.getMinDate();
    this.createForm = this.fb.group({
      sapID: ['', [Validators.required]],
      type: ['', Validators.required],
      startDate: [
        '',
        [Validators.required, dateMinMonthValidator(this.minDate)],
      ],
      endDate: ['', [Validators.required, dateMinMonthValidator(this.minDate)]],
      currency: [''],
      guaranteed: [''],
      additionalPercentage: [''],
    });
    this.workShiftsForm = this.createWorkShift();
  }

  ngOnInit(): void {
    this.createForm.get('type')?.valueChanges.subscribe((typeValue: string) => {
      this.updateConditionalValidators(typeValue);
    });
    this.updateConditionalValidators(this.createForm.get('type')?.value);
    this.master = this.masterService.getMaster();
    this.ACTYPES = this.master['COMMERCIALAGREEMENT#TYPES'];
    this.createForm.get('sapID')?.valueChanges.subscribe((sapID) => {
      this.entity = null;
      this.getEntity(sapID);
    });
    if (this.aC) {
      this.loadFromExistingAc(this.aC);
    }
    this.createForm
      .get('guaranteed')
      ?.valueChanges.subscribe((guaranteed: string) => {
        const raw = String(guaranteed).replace(/\D/g, '');
        const number = Number(raw);
        const formatted = new Intl.NumberFormat('es-CL').format(number);
        if (formatted !== guaranteed) {
          this.createForm
            .get('guaranteed')
            ?.setValue(formatted, { emitEvent: false });
        }
      });

    this.createForm
      .get('currency')
      ?.valueChanges.pipe(
        startWith(''),
        pairwise() // Devuelve un array con [valorAnterior, valorActual]
      )
      .subscribe(([prevCurrency, currCurrency]) => {
        if (prevCurrency) {
          this.updateGuaranteedAmountValidators(currCurrency);
        }
      });
    this.createForm
      .get('additionalPercentage')
      ?.valueChanges.subscribe((value: string) => {
        let numeric = String(value).replace(/[^0-9]/g, '');
        if (!value.includes('%')) {
          numeric = numeric.slice(0, -1);
        }
        this.createForm
          .get('additionalPercentage')
          ?.setValue(`${numeric}%`, { emitEvent: false });
      });
  }

  private updateGuaranteedAmountValidators(currencyType: string) {
    const guaranteedControl = this.createForm.get('guaranteed');
    if (!guaranteedControl) return;
    const isFixedPlusVariable =
      this.business.toUpperCase() +
        this.commercialAgreementsTypes.FixedplusVariable ===
      this.createForm.get('type')?.value;
    let value = Number(String(guaranteedControl.value).replace(/\D/g, ''));
    guaranteedControl.clearValidators();
    if (currencyType === 'CLP') {
      const min = isFixedPlusVariable ? 0 : 100000;
      guaranteedControl.addValidators([
        Validators.required,
        formattedNumberRangeValidator(min, 99000000),
      ]);
      guaranteedControl.updateValueAndValidity();
      guaranteedControl.setValue(Math.floor(value * this.dolarValue));
    } else if (currencyType === 'USD') {
      const min = isFixedPlusVariable ? 0 : 104;
      guaranteedControl.addValidators([
        Validators.required,
        formattedNumberRangeValidator(min, 105319),
      ]);
      guaranteedControl.updateValueAndValidity();
      guaranteedControl.setValue(Math.floor(value / this.dolarValue));
    }
  }

  loadFromExistingAc(aC: CommercialAgreement) {
    let guaranteed: number = Number(aC.guaranteed);
    this.createForm.patchValue({
      sapID: this.sapIdPipe.transform(aC.sapId),
      type: aC.type,
      currency: aC.guaranteedUoM,
      guaranteed: new Intl.NumberFormat('es-CL').format(guaranteed),
      additionalPercentage: Number(aC.additionalPercentage) * 100 + '%',
    });
    this.listOfWorkShifts = [];
    if (Array.isArray(aC.contractualShifts)) {
      aC.contractualShifts.forEach((shiftStr: any) => {
        try {
          const shift = JSON.parse(shiftStr);
          const shiftGroup = {
            startTime: shift.startTime || '',
            duration: shift.duration || '',
            days: {
              Monday: shift.days?.Monday || false,
              Tuesday: shift.days?.Tuesday || false,
              Wednesday: shift.days?.Wednesday || false,
              Thursday: shift.days?.Thursday || false,
              Friday: shift.days?.Friday || false,
              Saturday: shift.days?.Saturday || false,
              Sunday: shift.days?.Sunday || false,
            },
          };
          this.listOfWorkShifts.push(shiftGroup);
        } catch (error) {
          console.error('Error parsing shift JSON:', error);
        }
      });
    }
  }

  async getEntity(sapId: string) {
    let sapIdFormatted = sapId.padStart(10, '0');
    let control = this.createForm.get('sapID');
    try {
      const resp = await this.commercialAgreementsService.getBySapId(
        sapIdFormatted
      );

      if (!resp || resp.business !== this.business) {
        control?.setErrors({
          sapIdNotFound: 'Sap Id no existe.',
        });
        return;
      }

      control?.setErrors(null);
      this.entity = resp;
    } catch (error) {
      control?.setErrors({
        sapIdNotFound: 'acuerdo comercial no está permitido para esta entidad.',
      });
    }
  }

  createWorkShift(): FormGroup {
    return this.fb.group({
      startTime: this.fb.group({
        startHour: [0, Validators.required],
        startMinute: [0, Validators.required],
      }),
      duration: ['', Validators.required],
      days: this.fb.group(
        {
          Monday: [false, Validators.required],
          Tuesday: [false, Validators.required],
          Wednesday: [false, Validators.required],
          Thursday: [false, Validators.required],
          Friday: [false, Validators.required],
          Saturday: [false, Validators.required],
          Sunday: [false, Validators.required],
        },
        { validators: atLeastOneDaySelectedValidator() }
      ),
    });
  }

  showCreate(): boolean {
    return this.listOfWorkShifts.length < 5;
  }

  addWorkShift(): void {
    if (this.workShiftsForm.invalid) {
      return;
    }
    if (this.listOfWorkShifts.length < 5) {
      if (this.listOfWorkShifts.length > 0) {
        this.validateWorkShift();
      } else {
        this.listOfWorkShifts.push({
          ...this.workShiftsForm.value,
          startTime: this.startTime,
        });
        this.workShiftsForm = this.createWorkShift();
      }
    }
  }

  addAC() {
    if (this.entity === null) {
      return;
    }
    let initialStatus: string =
      this.business.toUpperCase() + this.commercialAgreementsStatuces.pendingN1;
    //ultimo dia del mes seleccionado
    const [year, month] = this.createForm.value.endDate.split('-').map(Number);
    let endDate = new Date(year, month, 0);
    endDate.setUTCHours(23, 59, 59, 999);
    //creacion de acuerdo commercial
    let CommercialAgreement: CreateCommercialAgreementInput = {
      agreementId: window.crypto.randomUUID(),
      startTime: new Date(this.createForm.value.startDate).toISOString(),
      endTime: endDate.toISOString(),
      status: initialStatus,
      entityId: this.entity.licensePlate,
      sapId: this.entity.sapId,
      carrier: this.entity.company,
      business: this.entity.business,
      center: this.entity.originalCenter,
      modelTypeName: this.entity.__typename,
      capacity: this.entity.capacity,
      type: this.createForm.value.type,
      adjustable: false,
      createdBy: this.usersService.getActiveUser().email,
      updatedBy: this.usersService.getActiveUser().email,
      contractualShifts: this.getWorkShiftsAsString(),
      history: [
        JSON.stringify({
          updatedBy: this.usersService.getActiveUser().email,
          updatedAt: new Date().toISOString(),
          status: initialStatus,
          comment: '',
        }),
      ],
    };
    if (this.hasAdditionalPercent) {
      CommercialAgreement.additionalPercentage =
        Number(
          String(this.createForm.value.additionalPercentage).replace(
            /[^0-9]/g,
            ''
          )
        ) / 100;
    }
    if (this.hasGuaranteed) {
      CommercialAgreement.guaranteed = Number(
        String(this.createForm.value.guaranteed).replace(/\D/g, '')
      );
    }
    if (this.createForm.value.currency) {
      CommercialAgreement.guaranteedUoM = this.createForm.value.currency;
    }
    this.commercialAgreementsService.createCommercialAgreement(
      CommercialAgreement
    );
  }

  getWorkShiftsAsString(): string[] {
    let arr: string[] = [];
    this.listOfWorkShifts.forEach((object) => {
      arr.push(JSON.stringify(object));
    });
    return arr;
  }

  updateConditionalValidators(type: string): void {
    const guaranteedControl = this.createForm.get('guaranteed');
    const currency = this.createForm.get('currency');
    const additionalPercentageControl = this.createForm.get(
      'additionalPercentage'
    );
    // Resetear los validadores
    guaranteedControl?.clearValidators();
    currency?.clearAsyncValidators();
    additionalPercentageControl?.clearAsyncValidators();
    guaranteedControl?.clearAsyncValidators();
    currency?.clearValidators();
    additionalPercentageControl?.clearValidators();
    guaranteedControl?.setValue('');
    currency?.setValue('');
    additionalPercentageControl?.setValue('');
    this.hasAdditionalPercent = false;
    this.hasGuaranteed = false;
    //valida si el tipo de AC requiere monto garantizado
    const isFixedPlusVariable =
      this.business.toUpperCase() +
        this.commercialAgreementsTypes.FixedplusVariable ===
      type;
    const isMinimumInsured =
      this.business.toUpperCase() +
        this.commercialAgreementsTypes.minimumInsured ===
      type;
    if (isFixedPlusVariable || isMinimumInsured) {
      this.hasGuaranteed = true;
      const guaranteedMin = isFixedPlusVariable ? 0 : 100000;
      guaranteedControl?.setValidators([
        Validators.required,
        formattedNumberRangeValidator(guaranteedMin, 99000000),
      ]);
      currency?.setValidators([Validators.required]);
    }
    //valida si el tipo de AC requiere porcentaje adicional
    if (
      this.business.toUpperCase() +
        this.commercialAgreementsTypes.FixedplusVariable ===
      type
    ) {
      this.hasAdditionalPercent = true;
      additionalPercentageControl?.setValidators([
        Validators.required,
        percentRangeValidator(0, 99),
      ]);
    }
    // Actualizar estado de validación
    currency?.updateValueAndValidity();
    guaranteedControl?.updateValueAndValidity();
    additionalPercentageControl?.updateValueAndValidity();
  }

  removeWorkShift(i: any) {
    this.listOfWorkShifts.splice(i, 1);
  }

  validateWorkShift(): void {
    this.isValidating = true;
    this.hasError = false;
    this.workShiftService
      .validateWorkShift(
        {
          ...this.workShiftsForm.value,
          startTime: this.startTime,
        },
        this.listOfWorkShifts
      )
      .subscribe(
        (response: ValidateWorkShiftResponse) => {
          if (response.success && response.data?.valid) {
            //aqui flujo normal add work
            this.listOfWorkShifts.push({
              ...this.workShiftsForm.value,
              startTime: this.startTime,
            });
            //luego reset del formulario
            this.workShiftsForm = this.createWorkShift();
          } else {
            //error
            this.hasError = true;
          }
          if (!response.success) {
            console.log(response.message);
          }
          this.isValidating = false;
        },
        (error) => {
          console.log(error.error.message);
          this.isValidating = false;
        }
      );
  }

  getMinDate(): string {
    let today = new Date();
    if (today.getDate() <= 15) {
      today = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    }
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  get startTime(): string {
    const { startHour, startMinute } =
      this.workShiftsForm.get('startTime')?.value || {};
    const hour = startHour?.toString().padStart(2, '0') ?? '00';
    const minute = startMinute?.toString().padStart(2, '0') ?? '00';
    return `${hour}:${minute}:00`;
  }

  /**
   * Función de desecho del modal.
   * @param {string} value Tecla con que fue desechado.
   */
  d(value: string): void {
    this.modalDismissed.emit(value);
  }

  /**
   * Función de cierre del modal.
   * @param {string} value Tecla con que fue cerrado.
   */
  c(value: string): void {
    this.addAC();
    this.modalClosed.emit(value);
  }
  get sapID() {
    return this.createForm.get('sapID')!;
  }
}
